import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Input,
} from "@chakra-ui/react";
import { MdAdd as IcoAdd } from 'react-icons/md';
// Components
import CommonButton from "../common/button/button";
import Upload from "../upload/upload";
// Styles
import "./dynamicEditor.css";

const SortableItem = ({ mainId, modelType, id, item, index, onDelete, onChange, setIsDirty }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renderItemContent = () => {
    if (item.type === "text") {
      return `Descripció`;
    } else if (item.type === "audio") {
      return `Fitxers d'àudio`;
    } else if (item.type === "image") {
      return `Fitxers d'imatge`;
    } else if (item.type === "link") {
      return `Enllaç`;
    }
    return `Item: ${item.type}`;
  };

  const renderTextareaComponent = () => {
    return (
      <FormControl mt={2} isRequired>
        <Textarea
          className="maintenance-form-field"
          placeholder="Escriu una descripció"
          value={item.description || ""}
          onChange={(e) => {
            if (onChange) {
              onChange(index, { ...item, description: e.target.value });
              setIsDirty(true);
            }
          }}
          minHeight="300px"
        />          
      </FormControl>
    );
  };

  const renderUploadComponent = () => {
    return (
      <div className="dynamic-editor-item-upload-component">          
        <Upload 
          type={modelType}
          id={mainId}
          id2={item.id2}
          style={{ marginTop: "10px"}}
          onChange={(files) => {
            if (onChange) {
              onChange(index, { ...item, files });
            }
          }}
          onNewFile={(newFiles) => {
            onChange(index, { ...item, modified: true });
            setIsDirty(true);
          }}           
          onDeleteFile={(file) => {
            onChange(index, { ...item, modified: true });
            setIsDirty(true);
          }}           
          onUpdateFile={(file) => {
            onChange(index, { ...item, modified: true });
            setIsDirty(true);
          }} 
          showTextFile={item.type === "audio"}
        />
      </div>
    );
  };  

  const renderLinkComponent = () => {
    return (
      <>
        <FormControl mt={2} isRequired>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            URL
          </FormLabel>          
          <Input
            className="maintenance-form-field"
            placeholder="https://"
            type="url"            
            value={item.url || ""}
            onChange={(e) => {
              if (onChange) {
                onChange(index, { ...item, url: e.target.value });
                setIsDirty(true);
              }
            }}
            maxLength={1024}
          />
        </FormControl>
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Texte de l'enllaç
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Texte de l'enllaç"
            value={item.text || ""}
            onChange={(e) => {
              if (onChange) {
                onChange(index, { ...item, text: e.target.value });
                setIsDirty(true);
              }
            }}
            maxLength={255}
          />
        </FormControl>        
      </>
    );
  };

  // Set component to render
  const indentComponent = [];
  if (item.type === "text") {
    indentComponent.push(renderTextareaComponent());
  } else if (item.type === "audio" || item.type === "image") {
    indentComponent.push(renderUploadComponent());
  } else if (item.type === "link") {
    indentComponent.push(renderLinkComponent());
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="dynamic-editor-item"
    >
      <div style={{ display: "flex", gap: "12px", alignItems: "center", width: "100%" }}>
        <div 
          style={{ minWidth: "30px", textAlign: "center", fontWeight: "bold" }}
          {...attributes}
          {...listeners}
        >
          {index + 1}
        </div>
        <div 
          style={{ flex: 1 }}
          {...attributes}
          {...listeners}
        >
          <FormLabel mt={2} fontSize="sm" className="maintenance-form-label">
            {renderItemContent()}
          </FormLabel>          
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete(index);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "30px",
            height: "30px",
            fontSize: "20px",
            cursor: "pointer",
            color: "#999",
            borderRadius: "4px",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f0f0f0";
            e.currentTarget.style.color = "#333";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#999";
          }}
        >
          ✕
        </div>
      </div>
      {indentComponent}
    </div>
  );
};

const DynamicEditor = ({ mainId, modelType, items = [], onChange, setIsDirty }) => {
  const [selectedType, setSelectedType] = useState("text");

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);
    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    
    if (onChange) {
      onChange(reorderedItems);
      setIsDirty(true);
    }
  };

  const handleDelete = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    if (onChange) {
      onChange(updatedItems);
      setIsDirty(true);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now().toString(),
      type: selectedType,
      ...(selectedType === "text" ? { description: "" } : 
        selectedType === "link" ? { url: "", text: "" } : 
        { files: [], id2: Date.now().toString() }),
    };
    const updatedItems = [...items, newItem];
    if (onChange) {
      onChange(updatedItems);
      setIsDirty(true);
    }
  };

  const handleUpdateItem = (index, updatedItem) => {
    const updatedItems = [...items];
    updatedItems[index] = updatedItem;
    if (onChange) {
      onChange(updatedItems);
    }
  };

  return (
    <div className="dynamic-editor-wrapper">
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="dynamic-editor-items">
            {items.map((item, index) => (
              <SortableItem 
                mainId={mainId}
                modelType={modelType}
                key={item.id} 
                id={item.id} 
                item={item} 
                index={index}
                onDelete={handleDelete}
                onChange={handleUpdateItem}
                setIsDirty={setIsDirty}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", marginTop: items.length > 0 ? "12px" : "0px" }}>
        <FormControl w="150px">
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Nou item
          </FormLabel>
          <Select
            className="maintenance-form-field"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            iconColor="black"
          >
            <option value="text">Texte</option>
            <option value="audio">Àudio</option>
            <option value="image">Imatge</option>
            <option value="link">Enllaç</option>
          </Select>
        </FormControl>
        <CommonButton 
          onClick={handleAddItem}
          style={{
            width: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "8px",
          }}
          inverted={true}
        >
          <IcoAdd size={18} />
          <span>Afegir</span>
        </CommonButton>         
      </div>
    </div>
  );
};

export default DynamicEditor;
